import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UseOutletContextQuote } from './Quote';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { QuoteDA } from '../../../../services/admin/quote.service';
import { SettingsDA } from '../../../../services/admin/settings.service';
import { DunningPolicy } from '../../../../models/settings.dunning-policies.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { Error } from '../../../../models/error.model';
import { Message } from '../../../../models/message.model';
import { legendInvalid } from '../../../../utils/legend.data.util';
import { isValidObjectAttributes } from '../../../../scripts/compare.object.script';
import { getError, getMessage } from '../../../../languages/translations/response';
import { suffixLegend } from '../../../../scripts/name.legend.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';

import { foreignCurrencyDefault, foreignCurrencyPayload, isForeignCurrencyValid, ForeignCurrencyState } from '../../../../components/element/InputForeignCurrency';
import AppSaleFormCreate from '../component/SaleFormCreate';
import AppSaleFormRequirements from '../component/SaleFormRequirements';
import AppSaleFormSignatures from '../component/SaleFormSignatures';
import AppPreloader from '../../../../components/Preloader';

export interface QuoteCreatePageProps {};

let errorResponse: Error, messageResponse: Message;

const QuoteCreatePage: React.FunctionComponent<QuoteCreatePageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, treasuryAccountsForCompany, contactsForCompany, productsForCompany, servicesForCompany} = UseOutletContextQuote()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [customer, setCustomer] = useState({value: '', valid: false, object: null as any})
  const [concerning, setConcerning] = useState({value: '', valid: false})
  const [date, setDate] = useState({value: '', valid: false})
  const [validUntil, setValidUntil] = useState({value: '', valid: false})
  const [deadline, setDeadline] = useState({value: '', valid: false, object: null as any})
  const [currency, setCurrency] = useState({value: '', valid: false, object: null as any})
  const [treasuryAccount, setTreasuryAccount] = useState({value: '', valid: false, object: null as any})
  const [language, setLanguage] = useState({value: '', valid: false})
  const [discountType, setDiscountType] = useState({value: '', valid: false})
  const [discountValue, setDiscountValue] = useState({value: '', valid: false})
  const [requirements, setRequirements] = useState({list: false, value: [] as {type: string, description: string, contents: {detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string}[]}[], valid: [] as {type: boolean, description: boolean, content: boolean, contents: {detail: boolean, quantity: boolean, price_unit: boolean, tax: boolean, rebate_amount: boolean, unit: boolean}[]}[]})
  const [signatures, setSignatures] = useState({list: true, value: [] as string[], valid: [] as boolean[]})
  const [foreignCurrency, setForeignCurrency] = useState<ForeignCurrencyState>(foreignCurrencyDefault)
  const [dunningPolicyId, setDunningPolicyId] = useState('')
  const [dunningPolicies, setDunningPolicies] = useState<DunningPolicy[]>([])

  const submitQuoteCreate = async () => {
    setLoadIndicator('on')

    let requerimentsIsValid: boolean = requirements.valid.every((item) => isValidObjectAttributes(item))
    let signaturesIsValid: boolean = signatures.valid.every((item) => (item === true))

    if (companyForUser && customer.valid && concerning.valid && date.valid && validUntil.valid && currency.valid && language.valid && requirements.list && signatures.list && requerimentsIsValid && signaturesIsValid && isForeignCurrencyValid(foreignCurrency)) {
      let validUntilDate = `${validUntil.value}T23:59:59.999Z`
      let companyData = {uuid: companyForUser.uuid, name: companyForUser.name, logo: companyForUser.logo, address: companyForUser.address, phone: companyForUser.phone || '', email: companyForUser.email || '', website: companyForUser.website || '', vat_number: companyForUser.vat_number || ''}

      await QuoteDA.postQuote(companyForUser.id, customer.object, concerning.value, date.value, validUntilDate, companyForUser.currency, foreignCurrencyPayload(foreignCurrency), treasuryAccount.object, language.value, requirements.value, signatures.value, companyData, '', dunningPolicyId || null).then( (response) => {
        if (response.status === 201) {
          messageResponse = response.data

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            navigate('/main/sale/quote/list', {replace: true})
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!customer.value) legendInvalid('legend-contact-required')
        if (!concerning.value) legendInvalid('legend-concerning-required')
        if (!date.value) legendInvalid('legend-date-required')
        if (!validUntil.value) legendInvalid('legend-valid-until-required')
        if (!currency.value) legendInvalid('legend-currency-required')
        if (!treasuryAccount.value) legendInvalid('legend-treasury-account-required')
        if (!language.value) legendInvalid('legend-language-required')
        if (!discountValue.value) legendInvalid('legend-discount-required')
        if (!requirements.value) legendInvalid('legend-requirements-required')
        if (!requerimentsIsValid) {
          for (let i = 0; i < requirements.value.length; i++) {
            if (requirements.value[i].contents.length === 0) {
              legendInvalid(`legend-requirement-contents-required${suffixLegend(i)}`)
            } else {
              for (let j = 0; j < requirements.value[i].contents.length; j++) {
                if (!requirements.value[i].contents[j].detail) legendInvalid(`legend-requirement-content-detail-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].unit) legendInvalid(`legend-requirement-content-unit-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].quantity) legendInvalid(`legend-requirement-content-quantity-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].price_unit) legendInvalid(`legend-requirement-content-price-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].tax_rate) legendInvalid(`legend-requirement-content-tax-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].rebate_amount) legendInvalid(`legend-requirement-content-rebate-required${suffixLegend(i, j)}`)
              }
            }
          }
        }
        if (!signaturesIsValid) {
          for (let i = 0; i < signatures.value.length; i++) {
            if (!signatures.value[i]) legendInvalid(`legend-signature-required${suffixLegend(i)}`)
          }
        }
        setLoadIndicator('off')
      })
    }
  }

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.quotes, branch: lang.labels.createQuote}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (companyForUser) {
      SettingsDA.getDunningPolicies(companyForUser.id).then((response) => {
        if (response.status === 200) {
          const items = (response.data?.items ?? response.data ?? []) as DunningPolicy[]
          setDunningPolicies(items)
          setDunningPolicyId((prev) => {
            if (prev) return prev
            const def = items.find((p) => p.is_default)
            return def ? def.id : ''
          })
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && treasuryAccountsForCompany && contactsForCompany && productsForCompany && servicesForCompany
        ?
        <div className="form">
          <AppSaleFormCreate sale={"quote"} customer={customer} concerning={concerning} date={date} validUntil={validUntil} deadline={deadline} currency={currency} treasuryAccount={treasuryAccount} language={language} discountType={discountType} discountValue={discountValue} setCustomer={setCustomer} setConcerning={setConcerning} setDate={setDate} setValidUntil={setValidUntil} setDeadline={setDeadline} setCurrency={setCurrency} setTreasuryAccount={setTreasuryAccount} setLanguage={setLanguage} setDiscountType={setDiscountType} setDiscountValue={setDiscountValue} foreignCurrency={foreignCurrency} setForeignCurrency={setForeignCurrency} dunningPolicyId={dunningPolicyId} setDunningPolicyId={setDunningPolicyId} dunningPolicies={dunningPolicies}></AppSaleFormCreate>
          <AppSaleFormRequirements date={date.value} currency={companyForUser.currency} requirements={requirements} setRequirements={setRequirements} foreignCurrency={foreignCurrency}></AppSaleFormRequirements>
          <AppSaleFormSignatures signatures={signatures} setSignatures={setSignatures}></AppSaleFormSignatures>
          <div className="d-flex flex-stack flex-wrap flex-md-nowrap bg-light-primary border border-primary border-dashed rounded h-100 p-5">
            <div className="notice d-flex align-items-center mb-5 mb-md-0 me-0 me-md-5">
              <span className="svg-icon svg-icon-3tx svg-icon-primary me-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM16 13H13V10C13 9.4 12.6 9 12 9C11.4 9 11 9.4 11 10V13H8C7.4 13 7 13.4 7 14C7 14.6 7.4 15 8 15H11V18C11 18.6 11.4 19 12 19C12.6 19 13 18.6 13 18V15H16C16.6 15 17 14.6 17 14C17 13.4 16.6 13 16 13Z" fill="black" />
                  <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                </svg>
              </span>    
              <div className="d-grid">
                <span className="text-start text-dark fw-bolder fs-5">{lang.labels.processOfCreatingQuoteCompleted}</span>
                <span className="text-justify text-muted fw-bold">
                  {lang.labels.clickOn}
                  <span className="text-primary text-lowercase fw-bolder mx-1">{lang.labels.createQuote}</span>
                  {lang.labels.ifYouWantToSaveTheNewQuote}
                </span>
              </div>
            </div>
            <button className="btn btn-lg btn-primary fw-bolder w-100 w-md-300px" type="button" data-kt-indicator={loadIndicator} onClick={submitQuoteCreate}>
              <span className="indicator-label text-uppercase text-nowrap">{lang.labels.createQuote}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default QuoteCreatePage;
